/**
 * ChannelManagementModal.tsx
 *
 * Sovereign Channels Manager:
 * - Create and manage Public Channels, Private Groups, and Community Channels
 * - Assign and reassign channels to categories dynamically
 * - Allows users to be channel admins, invite members, set avatars, and manage personal agents
 */

import React, { useState, useEffect } from 'react';
import {
  globalChannelAuthorizationGuard,
  type ChannelRecord,
  type ChannelVisibility,
} from '../../mesh/channels/channel-authorization-guard.js';
import {
  channelAssignmentStore,
  type ChannelCategoryDef,
} from './channel-assignment-store.js';

interface ChannelManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  onChannelCreated?: (channelId: string) => void;
  onSelectChannel?: (channelId: string) => void;
}

export const ChannelManagementModal: React.FC<ChannelManagementModalProps> = ({
  isOpen,
  onClose,
  currentUserId,
  onChannelCreated,
  onSelectChannel,
}) => {
  const [activeTab, setActiveTab] = useState<'create' | 'assign' | 'categories'>('create');
  const [categories, setCategories] = useState<ChannelCategoryDef[]>([]);
  const [channelName, setChannelName] = useState('');
  const [topic, setTopic] = useState('');
  const [visibility, setVisibility] = useState<ChannelVisibility>('private_group');
  const [avatarEmoji, setAvatarEmoji] = useState('🔒');
  const [assignedCategory, setAssignedCategory] = useState('community');
  const [inviteUserId, setInviteUserId] = useState('');
  const [invitedMembers, setInvitedMembers] = useState<string[]>([]);
  const [enablePersonalAgent, setEnablePersonalAgent] = useState(false);
  const [createdNotice, setCreatedNotice] = useState<string | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');

  const guard = globalChannelAuthorizationGuard;

  useEffect(() => {
    const updateCats = () => {
      setCategories(channelAssignmentStore.getCategories());
    };
    updateCats();
    channelAssignmentStore.on('change', updateCats);
    return () => {
      channelAssignmentStore.off('change', updateCats);
    };
  }, []);

  if (!isOpen) return null;

  const handleAddInvite = () => {
    if (inviteUserId.trim() && !invitedMembers.includes(inviteUserId.trim())) {
      setInvitedMembers([...invitedMembers, inviteUserId.trim()]);
      setInviteUserId('');
    }
  };

  const handleRemoveInvite = (userId: string) => {
    setInvitedMembers(invitedMembers.filter((id) => id !== userId));
  };

  const handleCreateChannel = (e: React.FormEvent) => {
    e.preventDefault();
    if (!channelName.trim()) return;

    const channelId = channelName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `chan-${Date.now().toString(36)}`;
    const membersMap = new Map<string, { role: 'owner' | 'admin' | 'member' | 'guest'; status: 'accepted' | 'invited' }>();

    // Current user is the channel owner & admin
    membersMap.set(currentUserId, { role: 'owner', status: 'accepted' });

    // Add invited users
    invitedMembers.forEach((id) => {
      membersMap.set(id, { role: 'member', status: 'invited' });
    });

    const newChannel: ChannelRecord = {
      channelId,
      tenantId: 'tenant-alpha',
      name: channelName.trim(),
      topic: topic.trim() || 'Sovereign channel discussion',
      visibility,
      avatarUrl: avatarEmoji,
      ownerPrincipalId: currentUserId,
      members: membersMap,
    };

    guard.registerChannel(newChannel);

    // Automatically assign to selected category
    channelAssignmentStore.assignChannelToCategory(channelId, assignedCategory || 'community');

    setCreatedNotice(`🎉 Channel "${channelName}" created! You are the Admin.`);
    setTimeout(() => {
      setCreatedNotice(null);
      onChannelCreated?.(channelId);
      onClose();
    }, 1000);
  };

  const handleReassign = (channelId: string, newCatId: string) => {
    channelAssignmentStore.assignChannelToCategory(channelId, newCatId);
  };

  const handleCreateCategory = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    const catId = channelAssignmentStore.addCategory(newCategoryName.trim());
    setNewCategoryName('');
    setAssignedCategory(catId);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="channel-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          backgroundColor: 'var(--theme-bg-card, #0f172a)',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: '14px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          color: 'var(--theme-text-primary, #f8fafc)',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>🎛️</span>
            <div>
              <h3 id="channel-modal-title" style={{ margin: 0, fontSize: '16px', fontWeight: 800 }}>
                Channel & Community Hub • Channels Manager
              </h3>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)' }}>
                Organize categories, assign channels, and manage access
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--theme-text-muted, #94a3b8)',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--theme-border, #1e293b)',
            backgroundColor: 'var(--theme-bg-subtle, #090d16)',
            padding: '0 16px',
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('assign')}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: 700,
              color: activeTab === 'assign' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'assign' ? '2px solid var(--theme-accent, #38bdf8)' : '2px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
            }}
          >
            📂 Assign & Organize
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('create')}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: 700,
              color: activeTab === 'create' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'create' ? '2px solid var(--theme-accent, #38bdf8)' : '2px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
            }}
          >
            ➕ New Channel
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('categories')}
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              fontWeight: 700,
              color: activeTab === 'categories' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-muted, #94a3b8)',
              borderBottom: activeTab === 'categories' ? '2px solid var(--theme-accent, #38bdf8)' : '2px solid transparent',
              background: 'none',
              borderTop: 'none',
              borderLeft: 'none',
              borderRight: 'none',
              cursor: 'pointer',
            }}
          >
            🏷️ Categories
          </button>
        </div>

        {/* Notice Alert */}
        {createdNotice && (
          <div
            style={{
              margin: '12px 20px 0 20px',
              padding: '8px 12px',
              backgroundColor: 'rgba(16, 185, 129, 0.15)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '6px',
              color: '#34d399',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {createdNotice}
          </div>
        )}

        {/* Modal Body */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
          {/* TAB 1: ASSIGN & ORGANIZE */}
          {activeTab === 'assign' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--theme-text-secondary, #cbd5e1)' }}>
                  Assign each channel to its sidebar category:
                </p>
                <button
                  type="button"
                  onClick={() => channelAssignmentStore.resetToDefaults()}
                  style={{
                    background: 'none',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '4px',
                    color: 'var(--theme-text-muted, #94a3b8)',
                    fontSize: '10px',
                    padding: '3px 8px',
                    cursor: 'pointer',
                  }}
                >
                  Reset Defaults
                </button>
              </div>

              {categories.map((cat) => (
                <div
                  key={cat.id}
                  style={{
                    backgroundColor: 'var(--theme-bg-subtle, #090d16)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '8px',
                    padding: '12px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      paddingBottom: '6px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: '12px', color: 'var(--theme-accent, #38bdf8)' }}>
                      {cat.name} ({cat.channelIds.length})
                    </span>
                    <span style={{ fontSize: '10px', color: 'var(--theme-text-muted, #64748b)' }}>ID: {cat.id}</span>
                  </div>

                  {cat.channelIds.length === 0 ? (
                    <div style={{ fontSize: '11px', color: 'var(--theme-text-muted, #64748b)', fontStyle: 'italic', padding: '4px' }}>
                      No channels assigned to this category.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {cat.channelIds.map((chId) => (
                        <div
                          key={chId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '6px 10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.04)',
                            borderRadius: '6px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#94a3b8', fontFamily: 'monospace' }}>#{chId}</span>
                            {chId === 'wall' && (
                              <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99, 102, 241, 0.2)', color: '#a5b4fc', fontWeight: 700 }}>
                                SOVEREIGN WALL
                              </span>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Reassign category dropdown */}
                            <select
                              value={cat.id}
                              onChange={(e) => handleReassign(chId, e.target.value)}
                              aria-label={`Reassign ${chId}`}
                              style={{
                                padding: '3px 8px',
                                borderRadius: '4px',
                                backgroundColor: '#1e293b',
                                border: '1px solid #334155',
                                color: '#f8fafc',
                                fontSize: '11px',
                                cursor: 'pointer',
                                outline: 'none',
                              }}
                            >
                              {categories.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>

                            {onSelectChannel && (
                              <button
                                type="button"
                                onClick={() => {
                                  onSelectChannel(chId);
                                  onClose();
                                }}
                                style={{
                                  background: 'none',
                                  border: '1px solid var(--theme-border, #1e293b)',
                                  borderRadius: '4px',
                                  color: 'var(--theme-accent, #38bdf8)',
                                  fontSize: '10px',
                                  padding: '3px 6px',
                                  cursor: 'pointer',
                                }}
                              >
                                Open
                              </button>
                            )}

                            {chId !== 'general' && chId !== 'wall' && (
                              <button
                                type="button"
                                onClick={() => channelAssignmentStore.removeChannel(chId)}
                                title="Remove from sidebar"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  padding: '2px 4px',
                                }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* TAB 2: CREATE CHANNEL */}
          {activeTab === 'create' && (
            <form onSubmit={handleCreateChannel} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                  Channel Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Executive Strategy, creators-hub, design-mesh"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  autoFocus
                  required
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                  Topic / Purpose
                </label>
                <input
                  type="text"
                  placeholder="What is this channel about?"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Assign to Category */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                  Assign to Category
                </label>
                <select
                  value={assignedCategory}
                  onChange={(e) => setAssignedCategory(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Visibility Options */}
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '6px' }}>
                  Channel Access Policy
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setVisibility('public')}
                    style={{
                      padding: '10px',
                      borderRadius: '8px',
                      backgroundColor: visibility === 'public' ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                      border: visibility === 'public' ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
                      color: 'var(--theme-text-primary, #f8fafc)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '12px', color: visibility === 'public' ? '#38bdf8' : '#f8fafc' }}>
                      🌐 Public Mesh
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                      Discoverable by all nodes and community members.
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setVisibility('private_group')}
                    style={{
                      padding: '10px',
                      borderRadius: '8px',
                      backgroundColor: visibility === 'private_group' ? 'rgba(56, 189, 248, 0.15)' : 'transparent',
                      border: visibility === 'private_group' ? '1px solid var(--theme-accent, #38bdf8)' : '1px solid var(--theme-border, #1e293b)',
                      color: 'var(--theme-text-primary, #f8fafc)',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '12px', color: visibility === 'private_group' ? '#38bdf8' : '#f8fafc' }}>
                      🔒 Private Group
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                      Strictly invitation-only with end-to-end access control.
                    </div>
                  </button>
                </div>
              </div>

              {/* Member Invites */}
              {visibility !== 'public' && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: 'var(--theme-text-muted, #94a3b8)', marginBottom: '4px' }}>
                    Invite Members
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Enter user handle (e.g. @alice, @bob)"
                      value={inviteUserId}
                      onChange={(e) => setInviteUserId(e.target.value)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        backgroundColor: 'var(--theme-bg-input, #1e293b)',
                        border: '1px solid var(--theme-border, #1e293b)',
                        borderRadius: '6px',
                        color: 'var(--theme-text-primary, #f8fafc)',
                        fontSize: '12px',
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleAddInvite}
                      style={{
                        padding: '8px 14px',
                        backgroundColor: 'var(--theme-bg-card, #151d2a)',
                        border: '1px solid #38bdf8',
                        borderRadius: '6px',
                        color: '#38bdf8',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      + Invite
                    </button>
                  </div>

                  {invitedMembers.length > 0 && (
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                      {invitedMembers.map((id) => (
                        <span
                          key={id}
                          style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: '#1e293b',
                            border: '1px solid #334155',
                            color: '#cbd5e1',
                            fontSize: '11px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          {id}
                          <button
                            type="button"
                            onClick={() => handleRemoveInvite(id)}
                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Submit Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'transparent',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-secondary, #cbd5e1)',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 20px',
                    backgroundColor: 'var(--theme-accent, #38bdf8)',
                    color: '#0b0f19',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  Create Channel
                </button>
              </div>
            </form>
          )}

          {/* TAB 3: CATEGORIES */}
          {activeTab === 'categories' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <form onSubmit={handleCreateCategory} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="New Category Name (e.g. Creator Studios, Gaming, Audio)"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    backgroundColor: 'var(--theme-bg-input, #1e293b)',
                    border: '1px solid var(--theme-border, #1e293b)',
                    borderRadius: '6px',
                    color: 'var(--theme-text-primary, #f8fafc)',
                    fontSize: '12px',
                  }}
                />
                <button
                  type="submit"
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'var(--theme-accent, #38bdf8)',
                    color: '#0b0f19',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 700,
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  + Add Category
                </button>
              </form>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories.map((c) => (
                  <div
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: 'var(--theme-bg-subtle, #090d16)',
                      border: '1px solid var(--theme-border, #1e293b)',
                      borderRadius: '6px',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, fontSize: '12px' }}>{c.name}</span>
                      <span style={{ fontSize: '10px', color: '#64748b', marginLeft: '8px' }}>({c.channelIds.length} channels)</span>
                    </div>
                    <span style={{ fontSize: '10px', color: '#64748b', fontFamily: 'monospace' }}>{c.id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
